import { useState } from "react";
import { alpha2ToAlpha3, subdivisionsForCountry } from "@connect/i18n";
import type { StructuredAddress } from "@connect/component-library/components/address-autocomplete";
import { isValidPostCode } from "./validate-postcode";

// Manual billing-address draft, lifted so the billing fields and footer can
// render in separate animated regions while sharing state.
export function useBillingDraft(country: string) {
  // Seed the country from the integrator's config so a manual address never
  // ships an empty country. The config default is empty, so fall back to GB.
  const seedCountry = country || "GB";
  const empty: StructuredAddress = {
    street: "",
    town: "",
    postCode: "",
    country: { alpha3: alpha2ToAlpha3(seedCountry), name: "" },
  };

  const [draft, setDraft] = useState<StructuredAddress | null>(null);

  const reset = (initialAddress: StructuredAddress | null) => {
    setDraft(initialAddress ?? empty);
  };

  const patch = (next: Partial<StructuredAddress>) =>
    setDraft((current) => ({ ...(current ?? empty), ...next }));

  const subdivisionCountry = draft?.country?.alpha3 ?? seedCountry;
  const subdivisions = subdivisionsForCountry(subdivisionCountry);
  const stateOptions = subdivisions.map((s) => ({ value: s.code, label: s.name }));
  const showState = stateOptions.length > 0;

  const handleStateChange = (code: string) => {
    const match = subdivisions.find((s) => s.code === code);
    patch({ state: code ? { code, name: match?.name ?? "" } : undefined });
  };

  const handleCountryChange = (alpha3: string, name: string) => {
    // Changing country invalidates the previous country's state selection.
    patch({ country: { alpha3, name }, state: undefined });
  };

  const ready =
    draft !== null &&
    draft.street.trim().length > 0 &&
    draft.town.trim().length > 0 &&
    draft.postCode.trim().length > 0 &&
    isValidPostCode(subdivisionCountry, draft.postCode) &&
    // When the country has subdivisions (e.g. US, CA) the State/Province
    // selector is shown and a selection is required before the address is valid.
    (!showState || !!draft.state?.code);

  return {
    draft,
    reset,
    patch,
    stateOptions,
    showState,
    handleStateChange,
    handleCountryChange,
    ready,
  };
}
